import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Flame, 
  Award, 
  TrendingUp, 
  Crown,
  Star,
  Sparkles,
  ArrowLeft,
  Download,
  Share2,
  Zap,
  Target,
  Trophy,
  Heart,
  Rocket,
  Lightning
} from 'lucide-react';

const SharedBadge = () => {
  const { badgeData } = useParams();
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const decodedData = JSON.parse(decodeURIComponent(badgeData));
      setBadge(decodedData);
      setLoading(false);
    } catch (err) {
      setError('Invalid badge data');
      setLoading(false);
    }
  }, [badgeData]);

  const getBadgeIcon = (theme) => {
    switch (theme) {
      case 'wizard':
        return <Sparkles className="w-8 h-8" />;
      case 'platinum':
        return <Crown className="w-8 h-8" />;
      case 'diamond':
        return <Star className="w-8 h-8" />;
      case 'gold':
        return <Trophy className="w-8 h-8" />;
      case 'silver':
        return <Award className="w-8 h-8" />;
      case 'bronze':
        return <Flame className="w-8 h-8" />;
      default:
        return <Rocket className="w-8 h-8" />;
    }
  };

  const getDynamicMessage = (streak, badgeName) => {
    const messages = {
      'Beginner Badge': [
        "🚀 Your learning journey has begun!",
        "🌟 Every expert was once a beginner",
        "💪 Small steps lead to big achievements",
        "🎯 Consistency is your superpower"
      ],
      'Bronze Badge': [
        "🔥 You're building momentum!",
        "⚡ Consistency is becoming your habit",
        "🎉 A week of dedication - impressive!",
        "💎 You're on the path to greatness"
      ],
      'Silver Badge': [
        "🥈 A month of excellence!",
        "🌟 You're developing real discipline",
        "💫 Your future self thanks you",
        "🎯 You're becoming unstoppable"
      ],
      'Gold Badge': [
        "🥇 Two months of pure dedication!",
        "🌟 You're in the top tier of learners",
        "💎 Your commitment is inspiring",
        "🚀 You're achieving the extraordinary"
      ],
      'Platinum Badge': [
        "💎 100 days of brilliance!",
        "🌟 You're a learning legend",
        "💫 Your persistence is legendary",
        "🎯 You've mastered consistency"
      ],
      'Diamond Badge': [
        "💎 200 days of excellence!",
        "🌟 You're a true learning champion",
        "💫 Your dedication is unmatched",
        "🚀 You're inspiring others"
      ],
      'Wizard Badge': [
        "🧙‍♂️ A year of magical learning!",
        "🌟 You're a true knowledge wizard",
        "💫 Your wisdom is legendary",
        "🎯 You've achieved the impossible"
      ]
    };

    const badgeMessages = messages[badgeName] || messages['Beginner Badge'];
    return badgeMessages[Math.floor(Math.random() * badgeMessages.length)];
  };

  const getMotivationalQuote = (streak) => {
    const quotes = [
      "The only way to do great work is to love what you do. - Steve Jobs",
      "Success is not final, failure is not fatal: it is the courage to continue that counts. - Winston Churchill",
      "The future belongs to those who believe in the beauty of their dreams. - Eleanor Roosevelt",
      "Education is the most powerful weapon which you can use to change the world. - Nelson Mandela",
      "The expert in anything was once a beginner. - Helen Hayes",
      "Learning is a treasure that will follow its owner everywhere. - Chinese Proverb",
      "Knowledge is power. Information is liberating. - Kofi Annan",
      "The beautiful thing about learning is that nobody can take it away from you. - B.B. King"
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  };

  const getNextMilestone = (currentStreak) => {
    const milestones = [7, 30, 60, 100, 200, 365];
    return milestones.find(milestone => milestone > currentStreak) || 365;
  };

  const getProgressPercentage = (currentStreak) => {
    const nextMilestone = getNextMilestone(currentStreak);
    const previousMilestone = currentStreak < 7 ? 0 : 
                             currentStreak < 30 ? 7 :
                             currentStreak < 60 ? 30 :
                             currentStreak < 100 ? 60 :
                             currentStreak < 200 ? 100 : 200;
    return Math.min(((currentStreak - previousMilestone) / (nextMilestone - previousMilestone)) * 100, 100);
  };

  const getThemeStyles = (theme) => {
    switch (theme) {
      case 'wizard':
        return {
          card: 'bg-gradient-to-br from-purple-500 via-indigo-600 to-purple-700',
          text: 'text-white',
          border: 'border-purple-300',
          glow: 'shadow-purple-500/50'
        };
      case 'platinum':
        return {
          card: 'bg-gradient-to-br from-gray-400 via-slate-500 to-gray-600',
          text: 'text-white',
          border: 'border-gray-300',
          glow: 'shadow-gray-500/50'
        };
      case 'diamond':
        return {
          card: 'bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600',
          text: 'text-white',
          border: 'border-cyan-300',
          glow: 'shadow-cyan-500/50'
        };
      case 'gold':
        return {
          card: 'bg-gradient-to-br from-yellow-400 via-orange-500 to-amber-600',
          text: 'text-white',
          border: 'border-yellow-300',
          glow: 'shadow-yellow-500/50'
        };
      case 'silver':
        return {
          card: 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500',
          text: 'text-white',
          border: 'border-gray-300',
          glow: 'shadow-gray-500/50'
        };
      case 'bronze':
        return {
          card: 'bg-gradient-to-br from-orange-400 via-red-500 to-orange-600',
          text: 'text-white',
          border: 'border-orange-300',
          glow: 'shadow-orange-500/50'
        };
      default:
        return {
          card: 'bg-gradient-to-br from-green-400 via-blue-500 to-purple-600',
          text: 'text-white',
          border: 'border-green-300',
          glow: 'shadow-green-500/50'
        };
    }
  };

  const downloadBadge = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 800;
    canvas.height = 600;
    
    // Enhanced background gradient
    const gradient = ctx.createLinearGradient(0, 0, 800, 600);
    if (badge.theme === 'wizard') {
      gradient.addColorStop(0, '#8b5cf6');
      gradient.addColorStop(0.5, '#7c3aed');
      gradient.addColorStop(1, '#4f46e5');
    } else if (badge.theme === 'platinum') {
      gradient.addColorStop(0, '#6b7280');
      gradient.addColorStop(0.5, '#4b5563');
      gradient.addColorStop(1, '#374151');
    } else if (badge.theme === 'diamond') {
      gradient.addColorStop(0, '#06b6d4');
      gradient.addColorStop(0.5, '#0891b2');
      gradient.addColorStop(1, '#3b82f6');
    } else if (badge.theme === 'gold') {
      gradient.addColorStop(0, '#fbbf24');
      gradient.addColorStop(0.5, '#f59e0b');
      gradient.addColorStop(1, '#f97316');
    } else if (badge.theme === 'silver') {
      gradient.addColorStop(0, '#d1d5db');
      gradient.addColorStop(0.5, '#9ca3af');
      gradient.addColorStop(1, '#6b7280');
    } else if (badge.theme === 'bronze') {
      gradient.addColorStop(0, '#fb923c');
      gradient.addColorStop(0.5, '#ea580c');
      gradient.addColorStop(1, '#ef4444');
    } else {
      gradient.addColorStop(0, '#4ade80');
      gradient.addColorStop(0.5, '#22c55e');
      gradient.addColorStop(1, '#3b82f6');
    }
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 800, 600);
    
    // Add subtle geometric patterns instead of circles
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    for (let i = 0; i < 800; i += 60) {
      for (let j = 0; j < 600; j += 60) {
        if ((i + j) % 120 === 0) {
          ctx.fillRect(i, j, 20, 20);
        }
      }
    }
    
    // Add sparkle effects
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    const sparkles = [
      { x: 100, y: 150, size: 3 },
      { x: 700, y: 200, size: 2 },
      { x: 200, y: 400, size: 4 },
      { x: 600, y: 350, size: 2 },
      { x: 300, y: 100, size: 3 },
      { x: 500, y: 500, size: 2 }
    ];
    
    sparkles.forEach(sparkle => {
      ctx.beginPath();
      ctx.arc(sparkle.x, sparkle.y, sparkle.size, 0, 2 * Math.PI);
      ctx.fill();
    });
    
    // Add border glow effect
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, 792, 592);
    
    // Student name at the top with enhanced typography
    ctx.fillStyle = 'white';
    ctx.font = 'bold 52px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 12;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    ctx.fillText(badge.userName || 'Student', 400, 120);
    
    // Congratulatory message with highlighted styling
    ctx.font = 'bold 36px "Segoe UI", Arial, sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.shadowBlur = 6;
    ctx.fillText('🎉 Congratulations! 🎉', 400, 170);
    
    // Badge name with enhanced styling and highlight
    ctx.font = 'bold 64px "Segoe UI", Arial, sans-serif';
    ctx.fillStyle = 'white';
    ctx.shadowBlur = 10;
    ctx.fillText(`${badge.badge} Badge`, 400, 250);
    
    // Streak count with dramatic styling and highlight
    ctx.font = 'bold 140px "Segoe UI", Arial, sans-serif';
    ctx.fillStyle = 'white';
    ctx.shadowBlur = 18;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.fillText(badge.streak.toString(), 400, 400);
    
    // Days text with highlighted styling
    ctx.font = 'bold 42px "Segoe UI", Arial, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.shadowBlur = 6;
    ctx.fillText(badge.streak === 1 ? 'day' : 'days', 400, 450);
    
    // Motivational text with enhanced highlight
    ctx.font = 'bold 32px "Segoe UI", Arial, sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.shadowBlur = 4;
    ctx.fillText('🔥 Keep the streak alive! 🔥', 400, 500);
    
    // Website branding with modern styling
    ctx.font = 'bold 28px "Segoe UI", Arial, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.shadowBlur = 3;
    ctx.fillText('mahstry.com', 400, 560);
    
    // Add a subtle pattern overlay
    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
    for (let i = 0; i < 800; i += 30) {
      for (let j = 0; j < 600; j += 30) {
        if ((i + j) % 60 === 0) {
          ctx.fillRect(i, j, 3, 3);
        }
      }
    }
    
    // Download the image
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `study-badge-${badge.badge.toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const shareBadge = async () => {
    const shareText = `🔥 Check out this ${badge.badge} Badge with a ${badge.streak}-day study streak! ${badge.message}`;
    const shareURL = window.location.href;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Study Streak Badge',
          text: shareText,
          url: shareURL
        });
      } else {
        await navigator.clipboard.writeText(`${shareText}\n\nView badge: ${shareURL}`);
      }
    } catch (error) {
      // Error sharing
      try {
        await navigator.clipboard.writeText(`${shareText}\n\nView badge: ${shareURL}`);
      } catch (clipboardError) {
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !badge) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Badge Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">The badge you're looking for doesn't exist or has expired.</p>
          <Link 
            to="/"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const themeStyles = getThemeStyles(badge.theme);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Enhanced Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-12"
        >
          <Link 
            to="/"
            className="inline-flex items-center px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
          <div className="flex space-x-3">
            <button
              onClick={downloadBadge}
              className="inline-flex items-center px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg transition-all duration-300"
            >
              <Download className="w-5 h-5 mr-2" />
              Download
            </button>
            <button
              onClick={shareBadge}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 hover:shadow-lg transition-all duration-300"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share
            </button>
          </div>
        </motion.div>

        {/* Enhanced Badge Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className={`relative rounded-3xl shadow-2xl overflow-hidden ${themeStyles.card} transform hover:scale-105 transition-transform duration-500`}
        >
          {/* Enhanced background pattern without circles */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-32 h-32 bg-white/20 rounded-lg animate-pulse"></div>
            <div className="absolute bottom-10 left-10 w-24 h-24 bg-white/20 rounded-lg animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/20 rounded-lg animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          
          <div className="relative p-16 text-center">
            {/* Enhanced Dynamic Congratulatory Message with highlighted fonts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-12"
            >
              <div className="text-4xl font-black text-yellow-300 mb-6 tracking-wide">
                🎉 <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">Congratulations!</span> 🎉
              </div>
              <div className="text-2xl text-white/95 font-semibold leading-relaxed">
                {getDynamicMessage(badge.streak, `${badge.badge} Badge`)}
              </div>
            </motion.div>
            
            {/* Enhanced Badge Name with highlighted styling */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-7xl font-black mb-12 text-white drop-shadow-2xl tracking-tight"
            >
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                {badge.badge} Badge
              </span>
            </motion.h1>
            
            {/* Enhanced Streak Count with dramatic styling and pulse effect */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 0.8, type: "spring" }}
              className="relative mb-12"
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="text-9xl font-black text-white drop-shadow-2xl tracking-tighter"
              >
                <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                  {badge.streak}
                </span>
              </motion.div>
              
              {/* Enhanced glow effect behind the number */}
              <div className="absolute inset-0 text-9xl font-black text-white/30 blur-sm -z-10 tracking-tighter">
                {badge.streak}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-4xl mb-12 text-white/95 font-bold tracking-wide"
            >
              <span className="bg-gradient-to-r from-white to-yellow-100 bg-clip-text text-transparent">
                {badge.streak === 1 ? 'day' : 'days'} active
              </span>
            </motion.div>
            
            {/* Enhanced Progress to next milestone */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mb-12"
            >
              <div className="text-xl text-white/90 mb-4 font-semibold">
                Progress to next milestone
              </div>
              <div className="w-full bg-white/20 rounded-full h-5 mb-4">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${getProgressPercentage(badge.streak)}%` }}
                  transition={{ delay: 1, duration: 1.5, ease: "easeOut" }}
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 h-5 rounded-full shadow-lg"
                ></motion.div>
              </div>
              <div className="text-base text-white/80 font-medium">
                {badge.streak} / {getNextMilestone(badge.streak)} days
              </div>
            </motion.div>
            
            {/* Enhanced Message with highlighted fonts */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="text-3xl opacity-95 text-white font-bold leading-relaxed mb-12"
            >
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                {badge.message}
              </span>
            </motion.p>
            
            {/* Enhanced Motivational Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="bg-white/15 backdrop-blur-sm rounded-xl p-8 border border-white/25"
            >
              <div className="text-xl text-white/90 italic font-medium leading-relaxed">
                <span className="bg-gradient-to-r from-yellow-200 to-orange-200 bg-clip-text text-transparent">
                  "{getMotivationalQuote(badge.streak)}"
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Enhanced Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="mt-12 text-center"
        >
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-200 dark:border-gray-700 shadow-xl">
            <motion.div
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                🚀 Start Your Own Study Streak!
              </h2>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Set Goals</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Define your learning objectives and track your progress
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Build Habits</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Develop consistent study routines that stick
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Earn Rewards</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Unlock badges and celebrate your achievements
                </p>
              </div>
            </div>
            
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              Join thousands of students building consistent study habits and earning amazing badges. 
              Your journey to academic excellence starts here!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:from-blue-700 hover:to-purple-700 hover:shadow-2xl transition-all duration-300 text-xl font-bold transform hover:scale-105"
              >
                🎯 Get Started Free
              </Link>
              
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                🔍 Explore Features
              </Link>
            </div>
            
            {/* Social proof */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full border-2 border-white"></div>
                    <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-blue-500 rounded-full border-2 border-white"></div>
                    <div className="w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full border-2 border-white"></div>
                  </div>
                  <span>10,000+ students</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span>4.9/5 rating</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Heart className="w-4 h-4 text-red-500 fill-current" />
                  <span>Free forever</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SharedBadge;
